import React, { createContext, useContext, useEffect, useMemo } from "react";
import analyticsService from "../core/AnalyticsService";
import GA4Adapter from "../adapters/GA4Adapter";
import ConsoleAdapter from "../adapters/ConsoleAdapter";
import analyticsConfig from "../../config/analyticsConfig";

const AnalyticsContext = createContext();

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalyticsContext must be used within an AnalyticsProvider");
  }
  return context;
};

/**
 * Builds the adapter list for this environment and initializes the shared
 * AnalyticsService exactly once. Mount near the root (see App.js) alongside
 * the app's other cross-cutting providers (ChatHistory/Onboarding/Platforms).
 *
 * Swapping/adding a destination (GTM, Mixpanel, ...) means editing this list
 * only — nothing downstream (hooks, sagas, pages) changes.
 */
export const AnalyticsProvider = ({ children }) => {
  useEffect(() => {
    const adapters =
      analyticsConfig.isEnabled && analyticsConfig.ga4MeasurementId
        ? [new GA4Adapter({ measurementId: analyticsConfig.ga4MeasurementId, debug: analyticsConfig.debug })]
        : [new ConsoleAdapter()];

    analyticsService.init(adapters);
    // Intentionally empty deps: analyticsService.init() is a no-op after the
    // first call, and env-derived config never changes at runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({ analytics: analyticsService }), []);

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

export default AnalyticsContext;
